import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessagesSquare, ChevronDown, Lightbulb, Loader2 } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";
import { AnalystChat } from "@/components/analyst/AnalystChat";
import { FeedbackBox } from "@/components/analyst/FeedbackBox";
import { LessonItem } from "@/components/analyst/LessonItem";

const Section = ({ title, children }) => (
  <div className="space-y-2"><p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{title}</p>{children}</div>
);

export const AnalystPanel = ({ m, r }) => {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const id = `${m.id}-${r.number}`;
  const picks = r.scan?.suggestions || [];

  useEffect(() => {
    if (!open || data) return;
    api("get", `/analyst/races/${m.id}/${r.number}`).then(setData).catch((e) => toast.error(errMsg(e)));
  }, [open, data, api, m.id, r.number]);

  const set = (k) => (fn) => setData((d) => ({ ...d, [k]: typeof fn === "function" ? fn(d[k]) : fn }));
  const draft = async () => {
    setDrafting(true);
    try {
      const ls = await api("post", `/analyst/races/${m.id}/${r.number}/lessons/draft`);
      set("lessons")((x) => [...ls, ...x]);
      toast.success(ls.length ? `${ls.length} lesson${ls.length > 1 ? "s" : ""} suggested. Approve the ones worth keeping.` : "No new reusable lessons (duplicates of existing lessons are skipped).");
    } catch (e) { toast.error(errMsg(e)); }
    setDrafting(false);
  };

  return (
    <div className="mt-3 border-t border-white/5 pt-3">
      <button data-testid={`analyst-toggle-${id}`} onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 text-sm font-semibold text-slate-200 hover:text-white">
        <MessagesSquare className="w-4 h-4 text-blue-400" />Discuss with the analyst<span className="text-[10px] text-slate-500 font-normal">(private)</span>
        <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && !data && <p className="flex items-center gap-1.5 text-xs text-slate-400 mt-3"><Loader2 className="w-3.5 h-3.5 animate-spin" />Loading…</p>}
      {open && data && (
        <div className="mt-3 space-y-5" data-testid={`analyst-panel-${id}`}>
          <Section title="Chat"><AnalystChat m={m} r={r} messages={data.messages} setMessages={set("messages")} /></Section>
          <Section title="Feedback on selections"><FeedbackBox m={m} r={r} picks={picks} feedback={data.feedback} setFeedback={set("feedback")} /></Section>
          <Section title="Lessons for future races">
            <button data-testid={`analyst-draft-lessons-${id}`} onClick={draft} disabled={drafting} className="flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-amber-500/40 text-amber-300 text-xs font-semibold hover:bg-amber-500/10 disabled:opacity-50">
              {drafting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lightbulb className="w-3.5 h-3.5" />}Suggest lessons from this race
            </button>
            <p className="text-[11px] text-slate-500">Lessons only reach future scans after you approve them.</p>
            <ul className="space-y-2">{data.lessons.map((l) => <LessonItem key={l.id} l={l} onChange={(n) => set("lessons")((x) => x.map((y) => (y.id === n.id ? n : y)))} />)}</ul>
          </Section>
        </div>
      )}
    </div>
  );
};
